// Aliased to avoid shadowing the browser's global `Image` constructor.
import { Image as TiptapImage } from '@tiptap/extension-image';

export type ResizableImageAlign = 'left' | 'right' | 'center';

// The width is stored on the node as a numeric attribute and emitted as an HTML `width="…"` attribute on the
// <img> in getHTML() output — that's the resize signal every mainstream email client honours reliably.
function syncImageWidth(img: HTMLImageElement, width: number | string | null | undefined): void {
  if (width == null || width === '') {
    img.style.width = '';
    img.removeAttribute('width');
    return;
  }
  const numeric = typeof width === 'number' ? width : parseInt(String(width), 10);
  if (Number.isFinite(numeric)) {
    img.style.width = `${numeric}px`;
    img.setAttribute('width', String(numeric));
  }
}

// Applies the wrap/align styling to the NodeView wrapper so the editor previews the same layout the emitted
// HTML will produce. Left/right float takes the image out of flow and lets adjacent text wrap; center puts
// the image on its own visual line via block display + auto margins.
function syncImageAlign(wrapper: HTMLElement, align: ResizableImageAlign | null): void {
  wrapper.style.float = '';
  wrapper.style.display = '';
  wrapper.style.margin = '';
  if (align === 'left') {
    wrapper.style.float = 'left';
    wrapper.style.margin = '0 1rem 1rem 0';
  } else if (align === 'right') {
    wrapper.style.float = 'right';
    wrapper.style.margin = '0 0 1rem 1rem';
  } else if (align === 'center') {
    wrapper.style.display = 'block';
    wrapper.style.margin = '0 auto';
  }
}

// Renders inserted images inside a wrapper with a bottom-right drag handle so users can resize with the mouse.
export const ResizableImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const attr = element.getAttribute('width');
          if (!attr) { return null; }
          const parsed = parseInt(attr, 10);
          return Number.isFinite(parsed) ? parsed : null;
        },
        renderHTML: (attributes: Record<string, unknown>) => {
          const width = attributes['width'];
          if (width == null || width === '') { return {}; }
          return { width: String(width) };
        }
      },
      // Align controls text-wrap around the image. `left`/`right` float the image so surrounding text flows
      // beside it; `center` puts the image on its own visual line. Left/right are emitted as both the
      // (deprecated) `align` HTML attribute and inline styles so Outlook Desktop's Word renderer honours the
      // layout even where it botches CSS float — every other modern client reads the CSS.
      align: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const attr = element.getAttribute('align');
          if (attr === 'left' || attr === 'right' || attr === 'center') { return attr; }
          const style = element.getAttribute('style') ?? '';
          if (/float\s*:\s*left/i.test(style)) { return 'left'; }
          if (/float\s*:\s*right/i.test(style)) { return 'right'; }
          if (/display\s*:\s*block/i.test(style) && /margin\s*:\s*[^;]*auto/i.test(style)) { return 'center'; }
          return null;
        },
        renderHTML: (attributes: Record<string, unknown>) => {
          const align = attributes['align'] as ResizableImageAlign | null;
          if (!align) { return {}; }
          if (align === 'left') {
            return { align: 'left', style: 'float: left; margin: 0 1rem 1rem 0' };
          }
          if (align === 'right') {
            return { align: 'right', style: 'float: right; margin: 0 0 1rem 1rem' };
          }
          return { style: 'display: block; margin: 0 auto' };
        }
      }
    };
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      // Wrapper is a <span> (not <div>) so it can legally live inside the containing <p> when the image is
      // configured as an inline node — a block child would cause browsers to auto-close the paragraph.
      const wrapper = document.createElement('span');
      wrapper.className = 'rich-text-image-wrapper';
      wrapper.setAttribute('contenteditable', 'false');

      const img = document.createElement('img');
      img.className = 'rich-text-image';
      img.src = String(node.attrs['src'] ?? '');
      if (node.attrs['alt']) { img.alt = String(node.attrs['alt']); }
      if (node.attrs['title']) { img.title = String(node.attrs['title']); }
      syncImageWidth(img, node.attrs['width'] as number | null);
      syncImageAlign(wrapper, node.attrs['align'] as ResizableImageAlign | null);

      const handle = document.createElement('span');
      handle.className = 'rich-text-image-resize-handle';
      handle.setAttribute('aria-hidden', 'true');

      wrapper.appendChild(img);
      wrapper.appendChild(handle);

      let currentNode = node;

      const onHandleMouseDown = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = img.getBoundingClientRect().width;

        const onMouseMove = (moveEvent: MouseEvent): void => {
          const delta = moveEvent.clientX - startX;
          const newWidth = Math.max(32, Math.round(startWidth + delta));
          // Update the DOM live for immediate feedback; the ProseMirror doc attr is committed on mouseup.
          img.style.width = `${newWidth}px`;
        };

        const onMouseUp = (): void => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          const finalWidth = Math.round(img.getBoundingClientRect().width);
          const pos = typeof getPos === 'function' ? getPos() : undefined;
          if (typeof pos === 'number') {
            editor.view.dispatch(
              editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, width: finalWidth })
            );
          }
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

      handle.addEventListener('mousedown', onHandleMouseDown);

      return {
        dom: wrapper,
        update(updatedNode) {
          if (updatedNode.type.name !== 'image') { return false; }
          currentNode = updatedNode;
          img.src = String(updatedNode.attrs['src'] ?? '');
          if (updatedNode.attrs['alt']) { img.alt = String(updatedNode.attrs['alt']); } else { img.removeAttribute('alt'); }
          if (updatedNode.attrs['title']) { img.title = String(updatedNode.attrs['title']); } else { img.removeAttribute('title'); }
          syncImageWidth(img, updatedNode.attrs['width'] as number | null);
          syncImageAlign(wrapper, updatedNode.attrs['align'] as ResizableImageAlign | null);
          return true;
        },
        destroy() {
          handle.removeEventListener('mousedown', onHandleMouseDown);
        },
        // Keep ProseMirror from stealing the mousedown / drag events on the handle — otherwise the editor
        // starts its own selection drag while we are trying to resize.
        stopEvent(event) {
          return handle.contains(event.target as Node);
        }
      };
    };
  }
});
